import React, { ReactNode, useCallback, useEffect } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CrossIcon from '@/components/icons/Cross';
import { styled } from '@mui/system';
import { Blocker, useNavigate } from 'react-router-dom';

export type ModalDlgProps = Omit<DialogProps, 'maxWidth' | 'maxHeight' | 'title'> & {
  title?: ReactNode,
  open: boolean,
  width?: number | string,
  height?: number | string,
  maxWidth?: number | string,
  maxHeight?: number | string,
  onClose: () => void,
  disableEscapeKeyDown?: boolean,
  disableBackdropClosing?: boolean,
  blocksPage?: boolean,
  withoutHeader?: boolean,
  blockIgnoranceConfirmationText?: string,
  navBlocker?: Blocker;
};

const CustomDialog = styled(Dialog)({
  ['& .MuiPaper-root']: {
    borderRadius: '20px',
    border: '1px solid #DFE1E6',
    boxShadow: '0px 0px 20px 0px #0000004D',
    background: '#FFFFFF',
    maxWidth: 'unset',
  },
  ['& h2.MuiDialogTitle-root']: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: 0,
    color: '#091E42',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '23.44px',
    position: 'relative',
    height: '23px',

    ['& button']: {
      width: '32px',
      height: '32px',
      padding: '0px',
      position: 'absolute',
      right: '-4px',
      top: '-4px',
      fontSize: '20px',
      color: 'inherit',
      opacity: 0.2,
      ['&:hover']: {
        opacity: 1,
        backgroundColor: 'transparent',
      },
    },
  },
  ['& div.MuiDialogContent-root']: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px',
    gap: '20px',
    maxWidth: 'none',
  }
});

export default function ({
  title, open, onClose, children, maxWidth, maxHeight, navBlocker,
  width, height, disableEscapeKeyDown, disableBackdropClosing,
  blocksPage, withoutHeader = false, blockIgnoranceConfirmationText,
  ...props
}: ModalDlgProps) {
  const nav = useNavigate();

  useEffect(() => {
    if (navBlocker && navBlocker.state === 'blocked') {
      if (blockIgnoranceConfirmationText && window.confirm(blockIgnoranceConfirmationText)) {
        navBlocker.proceed();
        nav(navBlocker.location);
      } else {
        navBlocker.reset();
      }
    }
  }, [navBlocker, navBlocker?.state, blockIgnoranceConfirmationText, nav]);

  const blocker = useCallback((e: BeforeUnloadEvent) => {
    if (blocksPage) {
      if (!blockIgnoranceConfirmationText || !window.confirm(blockIgnoranceConfirmationText)) {
        e.preventDefault();
        e.stopPropagation();
        e.returnValue = true;
      }
    }
  }, [blocksPage]);

  useEffect(() => {
    if (open && blocksPage) {
      window.addEventListener('beforeunload', blocker);
      return () => {
        window.removeEventListener('beforeunload', blocker);
      };
    } else {
      window.removeEventListener('beforeunload', blocker);
    }
  }, [ open, blocksPage, blocker ]);

  const closeHandler = useCallback((event: any, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClosing && reason && reason === 'backdropClick') {
      return;
    }
    onClose();
  }, [ onClose, disableBackdropClosing ]);

  return (
    <CustomDialog
      {...props}
      open={open}
      onClose={closeHandler}
      PaperProps={{
        ...props.PaperProps,
        sx: {
          maxWidth,
          maxHeight,
          width,
          height,
          padding: withoutHeader ? '0px' : '20px',
          ...props.PaperProps?.sx,
        }}
      }
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {!withoutHeader && (
        <DialogTitle>
          {title}
          <IconButton
            aria-label="close"
            onClick={onClose}
          >
            <CrossIcon />
          </IconButton>
        </DialogTitle>
      )}
      <DialogContent>
        {children}
      </DialogContent>
    </CustomDialog>
  );
}